'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, BookOpen, Shield, Users, Feather } from 'lucide-react';

const rotatingWords = ['forgotten', 'suppressed', 'undocumented', 'erased', 'untold'];

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % rotatingWords?.length);
        setVisible(true);
      }, 300);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative overflow-hidden" style={{ minHeight: '92vh', backgroundColor: 'var(--background)' }}>
      {/* Layered atmospheric background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 70% 20%, #E05A4712 0%, transparent 55%), radial-gradient(ellipse 50% 80% at 10% 90%, #F39C1210 0%, transparent 50%), radial-gradient(ellipse 40% 40% at 90% 80%, #2C5E3B08 0%, transparent 50%)',
        }}
      />
      {/* Subtle grain texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E")`,
        }}
      />
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="grid lg:grid-cols-[1fr_420px] xl:grid-cols-[1fr_480px] gap-16 lg:gap-20 items-center">

          {/* Left — Main copy */}
          <div>
            {/* Eyebrow */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium mb-10 tracking-wide"
              style={{
                borderColor: '#E05A4744',
                backgroundColor: '#E05A470D',
                color: 'var(--primary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'var(--primary)' }} />
              Community-curated · Human-moderated · Open archive
            </div>

            <h1
              className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight mb-6"
              style={{ color: 'var(--foreground)' }}
            >
              African history
              <br />
              <span className="relative inline-block">
                <span
                  className="transition-all duration-300"
                  style={{
                    color: 'var(--primary)',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(8px)',
                    display: 'inline-block',
                  }}
                >
                  {rotatingWords?.[wordIndex]}
                </span>
              </span>
              <br />
              no longer.
            </h1>

            <p
              className="text-lg lg:text-xl leading-relaxed mb-4 max-w-xl"
              style={{ color: 'var(--muted-foreground)' }}
            >
              Griott is a living archive of Zimbabwean and African history — built by the community, verified by human curators, and open to everyone.
            </p>
            <p
              className="text-base leading-relaxed mb-10 max-w-xl"
              style={{ color: 'var(--muted-foreground)', opacity: 0.8 }}
            >
              Oral traditions, firsthand accounts, and sourced records are preserved with full transparency about what we know, what we don't, and where the gaps remain.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-14">
              <Link
                href="/sign-up-login"
                className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 shadow-sm"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                Join as a Contributor
                <ArrowRight size={16} />
              </Link>
              <Link
                href="/contributions"
                className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm border transition-all duration-150 active:scale-95"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
              >
                <BookOpen size={16} />
                Explore Stories
              </Link>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-5">
              {[
                { icon: Feather, label: 'Oral tradition respected' },
                { icon: Shield, label: 'Human moderation pipeline' },
                { icon: Users, label: 'Community corroboration' },
              ]?.map((item) => (
                <div key={item?.label} className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: '#E05A4718' }}
                  >
                    <item.icon size={13} style={{ color: 'var(--primary)' }} />
                  </div>
                  <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    {item?.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Bento value cards */}
          <div className="hidden lg:grid grid-cols-2 gap-3 auto-rows-min">
            {/* Tall card — Mission */}
            <div
              className="col-span-2 rounded-2xl border p-6 relative overflow-hidden"
              style={{
                backgroundColor: 'var(--card)',
                borderColor: '#E05A4733',
                boxShadow: '0 2px 12px rgba(224,90,71,0.08)',
              }}
            >
              <div
                className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, #E05A4718 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
              />
              <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                Our mission
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>
                To document the documented gaps in African history — and fill them with verified community knowledge before they disappear.
              </p>
            </div>

            {/* Stat card — Contributors */}
            <div
              className="rounded-2xl border p-5 flex flex-col justify-between"
              style={{
                backgroundColor: 'var(--card)',
                borderColor: 'var(--border)',
                boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
              }}
            >
              <div className="text-3xl font-bold tabular-nums mb-1" style={{ color: 'var(--primary)' }}>312</div>
              <div className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>
                Active contributors across the archive
              </div>
            </div>

            {/* Stat card — Stories */}
            <div
              className="rounded-2xl border p-5 flex flex-col justify-between"
              style={{
                backgroundColor: '#F39C1214',
                borderColor: '#F39C1233',
              }}
            >
              <div className="text-3xl font-bold tabular-nums mb-1" style={{ color: '#A06800' }}>1,847</div>
              <div className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>
                Published stories
              </div>
            </div>

            {/* Wide card — How it works */}
            <div
              className="col-span-2 rounded-2xl border p-5"
              style={{
                backgroundColor: 'var(--card)',
                borderColor: 'var(--border)',
                boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
              }}
            >
              <div className="text-xs font-mono uppercase tracking-widest mb-4" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                How stories work
              </div>
              <div className="flex items-start gap-3">
                {[
                  { step: '01', label: 'Submit', desc: 'Write your account, cite your sources' },
                  { step: '02', label: 'Review', desc: 'Human curators verify and classify' },
                  { step: '03', label: 'Publish', desc: 'Joins the permanent archive' },
                ]?.map((s, i) => (
                  <div key={s?.step} className="flex-1 flex items-start gap-2">
                    <span
                      className="text-xs font-mono mt-0.5 shrink-0"
                      style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
                    >
                      {s?.step}
                    </span>
                    <div>
                      <div className="text-xs font-semibold mb-0.5" style={{ color: 'var(--foreground)' }}>{s?.label}</div>
                      <div className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>{s?.desc}</div>
                    </div>
                    {i < 2 && (
                      <ArrowRight size={12} className="mt-1 shrink-0 opacity-30" style={{ color: 'var(--muted-foreground)' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, var(--background))' }}
      />
    </section>
  );
}